import * as React from 'react';
import {
  autocompleteEmptyRecipe,
  autocompleteItemRecipe,
  autocompleteListRecipe,
} from '../recipes/sheet.recipe';
import { Input, type InputProps } from './Input';
import { Spinner } from './Spinner';
import { useFieldControlProps } from './field-context';

export interface AutocompleteOption {
  /** Stable identifier passed back on selection. */
  id: string;
  /** Primary line. */
  label: string;
  /** Optional second line — an address's city and state, say. */
  description?: string;
}

export interface AutocompleteProps
  extends Omit<InputProps, 'onChange' | 'onSelect' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  /** Suggestions to show. The caller decides where these come from. */
  options: AutocompleteOption[];
  onSelect: (option: AutocompleteOption) => void;
  loading?: boolean;
  /** Shown when there are no options and the input is long enough to search. */
  emptyMessage?: string;
  /** Characters required before the list opens. */
  minLength?: number;
  /**
   * Whether `options` is the finished answer for the current `value`.
   *
   * Callers that filter a list they already hold leave this alone: for them, no
   * options always means no matches. A caller fetching asynchronously passes
   * `false` while a query is still pending, so the empty message waits for the
   * results instead of claiming there are none.
   */
  searched?: boolean;
}

/**
 * A text input with a suggestion list.
 *
 * Deliberately knows nothing about where suggestions come from. The app's
 * address field feeds it Google Places predictions; a customer picker could
 * feed it API results. Putting the Places SDK in here would make the design
 * system depend on Google Maps, which would then be a dependency of every app
 * that imports any component from it.
 *
 *   const [q, setQ] = useState('');
 *   const predictions = usePlacePredictions(q);   // app-side
 *   <Autocomplete value={q} onChange={setQ} options={predictions} onSelect={…} />
 *
 * Implements the combobox pattern: arrow keys move the active option, Enter
 * selects, Escape closes, and `aria-activedescendant` tells a screen reader
 * which option is highlighted without moving real focus off the input.
 */
export const Autocomplete = React.forwardRef<
  HTMLInputElement,
  AutocompleteProps
>(function Autocomplete(
  {
    value,
    onChange,
    options,
    onSelect,
    loading = false,
    emptyMessage = 'No matches',
    minLength = 3,
    searched = true,
    id,
    disabled,
    className,
    onKeyDown,
    onBlur,
    ...props
  },
  ref
) {
  const generated = React.useId();
  const field = useFieldControlProps({ id, disabled });
  const controlId = field.id ?? `${generated}-autocomplete`;
  const listId = `${generated}-list`;

  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const canSearch = value.trim().length >= minLength;
  const isOpen = open && canSearch;
  // An open list has to have something to say. `searched` is what separates a
  // finished search that found nothing — worth the empty message — from a
  // caller that has not answered yet, which otherwise renders "no matches" over
  // the field for the length of a debounce, and again on every refocus.
  const showList = isOpen && (loading || options.length > 0 || searched);

  // Reset the highlight whenever the option set changes, so Enter cannot
  // select a stale row that has shifted under the cursor.
  React.useEffect(() => setActive(-1), [options]);

  // Close when focus or a click leaves the whole control.
  React.useEffect(() => {
    if (!isOpen) return;
    const onDocumentDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentDown);
    return () => document.removeEventListener('mousedown', onDocumentDown);
  }, [isOpen]);

  const choose = (option: AutocompleteOption) => {
    onSelect(option);
    setOpen(false);
    setActive(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setOpen(true);
        return;
      }
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        if (options.length === 0) return -1;
        const next = current + delta;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
      return;
    }

    if (event.key === 'Enter' && isOpen && active >= 0 && options[active]) {
      // Only swallow Enter when a suggestion is highlighted, so the key still
      // submits the surrounding form the rest of the time.
      event.preventDefault();
      choose(options[active]);
      return;
    }

    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={containerRef} className="relay-ui-internal relative w-full">
      <Input
        ref={ref}
        id={controlId}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={showList ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          active >= 0 && options[active]
            ? `${listId}-${options[active].id}`
            : undefined
        }
        autoComplete="off"
        disabled={field.disabled}
        aria-invalid={field['aria-invalid']}
        aria-describedby={field['aria-describedby']}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        trailingIcon={loading ? <Spinner size="sm" tone="muted" /> : undefined}
        className={className}
        {...props}
      />

      {showList ? (
        <div id={listId} role="listbox" className={autocompleteListRecipe()}>
          {options.length === 0 ? (
            <p className={autocompleteEmptyRecipe()}>
              {loading ? 'Searching…' : emptyMessage}
            </p>
          ) : (
            options.map((option, index) => (
              <button
                key={option.id}
                id={`${listId}-${option.id}`}
                type="button"
                role="option"
                aria-selected={index === active}
                className={autocompleteItemRecipe({ active: index === active })}
                // mousedown, not click: blur would close the list first.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="font-sans text-body">{option.label}</span>
                {option.description ? (
                  <span className="font-sans text-caption text-muted-foreground">
                    {option.description}
                  </span>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
});
